
import { useState, useEffect, useCallback, useRef } from "react";
import { getCurrentDate,getFirstDayOfMonth ,parseDate} from "Utils/dateUtils";
import { useDateFilter } from "./useDateFilter";
import { SCREEN_KEYS } from "constants/screenKeys";




function useReportGrid(fetchFn, mapFn, pageSize = 10,extraParams = {}) {
  const isFirstRender = useRef(true);

 const { fromDate, toDate, setFromDate, setToDate } = useDateFilter(
    SCREEN_KEYS.STORE_ABSTRACT_REPORT,
    { fromDate: getFirstDayOfMonth(), toDate: getCurrentDate() }
  );

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value > toDate) setToDate(value);
  };

  const handleToDateChange = (value) => {
    if (value < fromDate) return;
    setToDate(value);
  };

  const [committedDates, setCommittedDates] = useState({
    from: getFirstDayOfMonth(),
    to:   getCurrentDate(),
  });

  // ── Grid / pagination state ────────────────────────────────────────────────
  const [rowData,         setRowData]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [currentPage,     setCurrentPage]     = useState(0);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalElements,   setTotalElements]   = useState(0);
  const [filteredCount,   setFilteredCount]   = useState(0);
  const [gridApi,         setGridApi]         = useState(null);

 

  // Debounce: commit dates 800 ms after user stops typing
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(0);
      setCommittedDates({ from: fromDate, to: toDate });
    }, 800);
    return () => clearTimeout(timer);
  }, [fromDate, toDate]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!committedDates.from || !committedDates.to) return;

    setLoading(true);
    setError(null);
    setRowData([]);

    try {
      const result = await fetchFn({
        from:  parseDate(committedDates.from),
        to:    parseDate(committedDates.to),
        page:  currentPage,
        size:  pageSize,
        ...extraParams,
      });
      const mapped = (result.content ?? []).map(mapFn);
      setRowData(mapped);
      setTotalPages(result.totalPages ?? 1);
      setTotalElements(result.totalElements ?? 0);
    } catch (err) {
      console.error("useReportGrid fetch error:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [committedDates, currentPage, fetchFn, mapFn, pageSize,JSON.stringify(extraParams)]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keep filteredCount in sync with totalElements when page changes
  useEffect(() => {
    setFilteredCount(totalElements);
  }, [totalElements, currentPage]);

  // ── AG Grid callbacks ──────────────────────────────────────────────────────
  const onGridReady = (params) => setGridApi(params.api);

  const onFilterChanged = useCallback(
    (params) => {
      if (!params?.api) return;
      const isActive = Object.keys(params.api.getFilterModel()).length > 0;
      setFilteredCount(isActive ? params.api.getDisplayedRowCount() : totalElements);
    },
    [totalElements]
  );

  // ── Pagination ─────────────────────────────────────────────────────────────
  const handlePrevious = () => { if (currentPage > 0) setCurrentPage((p) => p - 1); };
  const handleNext     = () => { if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1); };

  return {
    // date
    fromDate, toDate, handleFromDateChange, handleToDateChange,
    committedDates,
    // data
    rowData, loading, error,
    // pagination
    currentPage, totalPages, totalElements, pageSize,
    handlePrevious, handleNext,
    // grid
    gridApi, filteredCount, onGridReady, onFilterChanged,
  };
}

export default useReportGrid;